import config from '../config'
import { get } from './ApiService'

export interface LeanIndicatorFilters {
  projectId: string
  from?: string
  to?: string
  performerIds?: string[]
  groupIds?: string[]
  taskTypes?: string[]
  locations?: string[]
  restrictionFamilies?: string[]
  cncCategoryIds?: string[]
}

export interface LeanDrilldownRequest {
  projectId: string
  metric: 'ppc' | 'pcr' | 'cnc' | 'quality'
  periodKey?: string
  horizonWeeks?: number
  bucket?: string
  cursor?: string
  performerIds?: string[]
  groupIds?: string[]
  taskTypes?: string[]
  locations?: string[]
  restrictionFamilies?: string[]
  cncCategoryIds?: string[]
}

export type LeanIndicatorMetric = 'ppc' | 'pcr' | 'cnc' | 'quality'

export interface LeanIndicatorsResult {
  ppc: any | null
  pcr: any | null
  cnc: any | null
  quality: any[]
  errors: Partial<Record<LeanIndicatorMetric, true>>
}

const buildUrl = (metric: 'ppc' | 'pcr' | 'cnc', filters: LeanIndicatorFilters): string => {
  const query = new URLSearchParams({ project_id: filters.projectId })

  if (filters.from) query.set('from', filters.from)
  if (filters.to) query.set('to', filters.to)
  filters.performerIds?.forEach((value) => query.append('performer_ids', value))
  filters.groupIds?.forEach((value) => query.append('group_ids', value))
  filters.taskTypes?.forEach((value) => query.append('task_types', value))
  filters.locations?.forEach((value) => query.append('locations', value))
  filters.restrictionFamilies?.forEach((value) => query.append('restriction_families', value))
  filters.cncCategoryIds?.forEach((value) => query.append('cnc_category_ids', value))
  if (metric === 'pcr') {
    query.append('horizon_weeks', '1')
    query.append('horizon_weeks', '2')
  }

  return `${config.apiHost}/indicators/lean/${metric}?${query.toString()}`
}

const load = async (metric: 'ppc' | 'pcr' | 'cnc', filters: LeanIndicatorFilters): Promise<any> => {
  const response = await get(buildUrl(metric, filters), `LEAN_INDICATORS_LOAD_${metric.toUpperCase()}`)
  if (response.error) throw new Error(response.error)
  return response.data
}

export const loadLeanIndicators = async (filters: LeanIndicatorFilters): Promise<LeanIndicatorsResult> => {
  const qualityUrl = `${config.apiHost}/indicators/lean/data-quality?project_id=${encodeURIComponent(filters.projectId)}`
  const [ ppcResult, pcrResult, cncResult, qualityResult ] = await Promise.all([
    load('ppc', filters).then((value) => ({ value })).catch(() => ({ error: true as const })),
    load('pcr', filters).then((value) => ({ value })).catch(() => ({ error: true as const })),
    load('cnc', filters).then((value) => ({ value })).catch(() => ({ error: true as const })),
    get(qualityUrl, 'LEAN_INDICATORS_LOAD_DATA_QUALITY')
      .then((response) => response.error ? ({ error: true as const }) : ({ value: response.data?.data || [] }))
      .catch(() => ({ error: true as const })),
  ])

  const errors: Partial<Record<LeanIndicatorMetric, true>> = {}
  if ('error' in ppcResult) errors.ppc = true
  if ('error' in pcrResult) errors.pcr = true
  if ('error' in cncResult) errors.cnc = true
  if ('error' in qualityResult) errors.quality = true

  return {
    ppc: 'value' in ppcResult ? ppcResult.value : null,
    pcr: 'value' in pcrResult ? pcrResult.value : null,
    cnc: 'value' in cncResult ? cncResult.value : null,
    quality: 'value' in qualityResult ? qualityResult.value : [],
    errors,
  }
}

export const loadLeanDrilldown = async (request: LeanDrilldownRequest): Promise<any> => {
  const query = new URLSearchParams({ project_id: request.projectId, metric: request.metric })
  if (request.periodKey) query.set('period_key', request.periodKey)
  if (request.horizonWeeks) query.set('horizon_weeks', `${request.horizonWeeks}`)
  if (request.bucket) query.set('bucket', request.bucket)
  if (request.cursor) query.set('cursor', request.cursor)
  request.performerIds?.forEach((value) => query.append('performer_ids', value))
  request.groupIds?.forEach((value) => query.append('group_ids', value))
  request.taskTypes?.forEach((value) => query.append('task_types', value))
  request.locations?.forEach((value) => query.append('locations', value))
  request.restrictionFamilies?.forEach((value) => query.append('restriction_families', value))
  request.cncCategoryIds?.forEach((value) => query.append('cnc_category_ids', value))
  const response = await get(`${config.apiHost}/indicators/lean/drilldown?${query.toString()}`, 'LEAN_INDICATORS_LOAD_DRILLDOWN')
  if (response.error) throw new Error(response.error)
  return response.data
}
